import Layout from '@comp/layout'
import SolutionMeta from '@comp/solution/solution-meta'
import SolutionHero from '@comp/solution/solution-hero'
import SolutionPillars, { Pillar } from '@comp/solution/solution-pillars'
import ProductHowItWorks, { Step } from '@comp/product/product-how-it-works'
import SolutionRelated, { RelatedItem } from '@comp/solution/solution-related'
import FinalCta from '@comp/home/final-cta'

const pillars: Pillar[] = [
  {
    title: 'Events in, access out',
    description:
      'Triggers turn identity events into actions: a user joins a group, an attribute changes, an agent is suspended, and the right workflow runs, every time.',
    bullets: [
      'Event → action rules, configured not coded',
      'Assignment, workflow, and notification actions',
      'Notify the actor, subject, manager, or access owner',
      'One console for triggers, approvals, and justifications',
    ],
  },
  {
    title: 'Automation that can undo itself',
    description:
      'The dangerous part of automation is what it leaves behind. When the triggering event is reversed, Monosign compensates: derived access rolls back automatically.',
    bullets: [
      'Provenance tracked for every derived assignment',
      'Removal of the cause rolls back the effect',
      'External changes route to approval, not silent drift',
      'No orphaned access from long-dead rules',
    ],
  },
  {
    title: 'Write back to every system',
    description:
      'Source-aware routing sends each assignment where it belongs: internal membership, AD/LDAP write-back, or an entitlement connector into your SaaS estate.',
    bullets: [
      '49 ready entitlement connectors, write-back included',
      'Generic connectors: SCIM, REST, CSV, LDAP, database',
      'Code management, cloud IAM, collaboration, ITSM coverage',
      'Deprovision policies per target system',
    ],
  },
  {
    title: 'No-code, down to the last field',
    description:
      'The forms and links that feed your workflows are configurable without JSON or tickets: conditional logic, rich date rules, governed enrollment links.',
    bullets: [
      'Conditional show / hide between form fields',
      'Date-time fields with constraints, formats, timezones',
      'Magic links with per-link status and delivery visibility',
      'Duration-based link validity, tokens stored hashed',
    ],
  },
]

const steps: Step[] = [
  {
    title: 'Define the trigger',
    description:
      'Pick the event and the action: group membership grants an entitlement, a departure revokes it, a flag starts a workflow.',
  },
  {
    title: 'Route by source',
    description:
      'Monosign sends each assignment to the right mechanism: internal, directory write-back, or an external connector.',
  },
  {
    title: 'Trust the rollback',
    description:
      'When the cause disappears, the derived access does too; compensation is built into the engine, not bolted on.',
  },
]

const related: RelatedItem[] = [
  {
    label: 'User lifecycle',
    description:
      'Joiner / mover / leaver: the flows these triggers power.',
    href: '/sign/lifecycle',
  },
  {
    label: 'Reporting & timeline',
    description:
      'See every automated change on the Access Timeline.',
    href: '/sign/reporting',
  },
  {
    label: 'Monosync Governance',
    description:
      'Cross-app entitlement mapping and reconciliation.',
    href: '/sync/governance',
  },
]

export default function AutomationPage() {
  return (
    <Layout>
      <SolutionMeta
        title="Identity automation & entitlement connectors"
        description="Event-driven identity automation with built-in rollback: triggers, source-aware provisioning, 49 entitlement connectors with write-back, no-code forms, and governed magic links."
        path="/sign/automation"
      />
      <main className="bg-surface text-ink-primary">
        <SolutionHero
          eyebrow="Monosign · Automation"
          title={
            <>
              Automate access.
              <br />
              Keep the undo button.
            </>
          }
          body="Event-driven triggers grant, revoke, and notify across your whole estate, and every derived assignment carries provenance, so reversing the cause reverses the effect. Automation without the orphaned-access debt."
          badges={[
            'Rollback-safe triggers',
            '49 entitlement connectors',
            'No-code forms & flows',
          ]}
        />
        <SolutionPillars
          title={<>Provisioning logic your auditors can follow.</>}
          body="Every automated change knows why it happened and what caused it. That provenance is what makes rollback (and clean audits) possible."
          pillars={pillars}
        />
        <ProductHowItWorks
          title={<>From event to governed access in three steps.</>}
          steps={steps}
        />
        <SolutionRelated items={related} />
        <FinalCta />
      </main>
    </Layout>
  )
}
